import HubLeadCTA from "@/components/HubLeadCTA";
import { ctaDesc, ctaTitle } from "@/lib/seo";
import { createFileRoute, Link } from "@tanstack/react-router";
import { NOTARY_PRODUCTS, NOTARY_PRODUCT_CATEGORIES, NOTARY_CATALOG_PRICE_MIN, NOTARY_CATALOG_PRICE_MAX } from "@/data/notary-products";
import { BRAND_URL } from "@/lib/brand";
import { ProductCatalogHubNav } from "@/components/ProductCatalogHubNav";

const TITLE = "Thai Notary Public Services Catalog | Affidavit · POA · Certified Copy · NYC";
const DESC = `Full catalog of ${NOTARY_PRODUCTS.length} Thai Notarial Services — signature notarisation, Affidavits, POAs, corporate resolutions, certified translations. –${NOTARY_CATALOG_PRICE_MAX.toLocaleString} · 1 business day · registered with the Lawyers Council of Thailand. Send your documents for a free check.`;

export const Route = createFileRoute("/en/notary/products/")({
  head: () => {
    const url = `${BRAND_URL}/en/notary/products`;
    return {
      meta: [
        { title: ctaTitle(TITLE, "/en/notary/products/index", "en")}, { name: "description", content: ctaDesc(DESC, "/en/notary/products/index", "en")},
        { property: "og:title", content: ctaTitle(TITLE, "/en/notary/products/index", "en")}, { property: "og:description", content: ctaDesc(DESC, "/en/notary/products/index", "en")},
        { property: "og:url", content: url }, { property: "og:type", content: "website" },
      ],
      links: [
        { rel: "canonical", href: url },
        { rel: "alternate", hrefLang: "en", href: url },
        { rel: "alternate", hrefLang: "th-TH", href: `${BRAND_URL}/notary/products` },
        { rel: "alternate", hrefLang: "x-default", href: `${BRAND_URL}/notary/products` },
      ],
      scripts: [{
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [{
            "@type": "CollectionPage", "@id": `${url}#collection`,
            url, name: TITLE, inLanguage: "en",
            numberOfItems: NOTARY_PRODUCTS.length,
          }, {
            "@type": "ItemList", "@id": `${url}#itemlist`,
            itemListElement: NOTARY_PRODUCTS.map((p, i) => ({
              "@type": "ListItem", position: i + 1,
              url: `${BRAND_URL}/en/notary/products/${p.slug}`, name: p.nameEn,
            })),
          }, {
            "@type": "OfferCatalog", "@id": `${url}#catalog`, name: TITLE,
            itemListElement: NOTARY_PRODUCTS.map((p) => ({
              "@type": "Offer", name: p.nameEn, sku: p.sku,
              priceCurrency: "THB", price: p.priceMid,
              url: `${BRAND_URL}/en/notary/products/${p.slug}`,
            })),
          }],
        }),
      }],
    };
  },
  component: CatalogEN,
});

function CatalogEN() {
  return (
    <main className="min-h-screen bg-background">
      <section className="bg-gradient-to-b from-indigo-100/60 to-background px-4 pt-16 pb-10">
        <div className="mx-auto max-w-6xl">
          <span className="inline-block rounded-full bg-indigo-500/15 px-3 py-1 text-xs font-bold text-indigo-700">Notary Product Catalog</span>
          <h1 className="mt-3 font-display text-3xl font-extrabold text-navy-deep sm:text-5xl">Thai Notary Public — {NOTARY_PRODUCTS.length} services</h1>
          <p data-speakable className="direct-answer mt-4 max-w-3xl rounded-xl border-l-4 border-indigo-400 bg-indigo-50/50 p-4 text-[15px] leading-relaxed text-slate-700">
            From THB {NOTARY_CATALOG_PRICE_MIN.toLocaleString()}–{NOTARY_CATALOG_PRICE_MAX.toLocaleString()} · 1 business day standard · Registered with the Lawyers Council of Thailand — ready for MFA, Apostille, and any embassy chain.
          </p>
        </div>
      </section>
      <section className="mx-auto max-w-6xl px-4 py-10">
        {NOTARY_PRODUCT_CATEGORIES.map((c) => (
          <div key={c.id} className="mb-10">
            <h2 className="mb-4 font-display text-xl font-extrabold text-navy-deep">
              {c.labelEn} <span className="text-sm font-normal text-slate-500">({c.count} items)</span>
            </h2>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {NOTARY_PRODUCTS.filter((p) => p.category === c.id).map((p) => (
                <Link key={p.sku} to="/en/notary/products/$slug" params={{ slug: p.slug }}
                  className="group rounded-2xl border border-slate-200 bg-white p-5 transition hover:-translate-y-0.5 hover:border-indigo-400 hover:shadow-md">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-indigo-700">{p.sku}</div>
                  <div className="mt-1 font-display font-extrabold text-navy-deep group-hover:text-indigo-700">{p.nameEn}</div>
                  <div className="mt-2 text-xs text-slate-600 line-clamp-2">{p.shortDescEn}</div>
                  <div className="mt-3 text-xs font-semibold text-emerald-700">THB {p.priceMin.toLocaleString()}–{p.priceMax.toLocaleString()} · {p.act.turnaround}</div>
                </Link>
              ))}
            </div>
          </div>
        ))}
      </section>
      <ProductCatalogHubNav locale="en" current="notary" />
    <HubLeadCTA locale="en" service="Notary Public" />
      </main>
  );
}
